/*
 * Fiches accessoires (bloc _kt-accessoire), aux codes des fiches rideau (31/10/2026) :
 * - « Faites votre kit » : couleur, décoration (embout), fixation (pose ou support), autres options,
 *   en pastilles, photos et boutons rendus par le Liquid ;
 * - longueur : cartes « Taille standard » et « Sur mesure – Offert », comme les rideaux ;
 * - le récapitulatif (bloc _recap-product) reçoit une ligne par choix, la longueur et « Dans le colis ».
 * Le sélecteur du thème (<variant-input>), masqué, reste maître du choix de la variante : ce script
 * coche ses boutons et lui laisse recharger la section. Après chaque rechargement, le bloc du
 * sélecteur est remplacé : on le surveille pour relire la variante et remettre les choix d'aplomb.
 */
(() => {
  if (customElements.get("kt-accessoire")) return;

  const fmt = (s, map) => String(s || "").replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));
  /* Montant en centimes au format de la boutique (themeGlobalVariables.settings.money_format). */
  const money = (cents) => {
    const format = (window.themeGlobalVariables && themeGlobalVariables.settings && themeGlobalVariables.settings.money_format) || "{{amount_with_comma_separator}}€";
    const grouper = (n, d, sep, dec) => {
      const [ent, frac] = (n / 100).toFixed(d).split(".");
      return ent.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (frac ? dec + frac : "");
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => {
      switch (k) {
        case "amount": return grouper(cents, 2, ",", ".");
        case "amount_no_decimals": return grouper(cents, 0, ",", ".");
        case "amount_with_comma_separator": return grouper(cents, 2, ".", ",");
        case "amount_no_decimals_with_comma_separator": return grouper(cents, 0, ".", ",");
        case "amount_with_space_separator": return grouper(cents, 2, " ", ",");
        case "amount_with_apostrophe_separator": return grouper(cents, 2, "'", ".");
        default: return grouper(cents, 2, ",", ".");
      }
    });
  };
  const PARAMS = ["kt_o1", "kt_o2", "kt_o3", "kt_l", "kt_c"];
  const MIN_CM = 10;

  class KtAccessoire extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try {
        this.data = JSON.parse(this.querySelector(".kt-acc-data").textContent);
      } catch (e) {
        return;
      }
      this.cfg = this.data.config || {};
      this.t = this.data.t || {};
      this.sectionId = this.dataset.section;
      this.productId = this.dataset.product;
      this.root = document.getElementById("shopify-section-" + this.sectionId) || document;
      this.lenPos = Number(this.cfg.longueur || 0);
      this.morceau = Number(this.cfg.morceau || 0);
      this.unit = this.dataset.unit === "in" ? "in" : "cm";
      this.mode = "standard";
      this.cote = null;
      this.note = "";
      this.kit = this.querySelector(".kt-kit");
      this.bindKit();
      if (this.lenPos) this.setupLength();
      this.setupRecap();
      this.observe();
      this.refresh();
      // Le thème ne charge son sélecteur qu'à la première interaction : on le charge
      // nous-mêmes quand des choix arrivent par l'adresse (venue d'une autre couleur).
      if (PARAMS.some((k) => new URLSearchParams(location.search).has(k))) {
        this.picker().then(() => this.applyFromUrl());
      }
      this.bindSiblings();
      this.guardSubmit();
    }

    /* ---------- sélecteur du thème ---------- */
    fieldset(pos) {
      return this.root.querySelector(`#fieldset-${this.sectionId}-product-${this.productId}-position-${pos - 1}`);
    }
    labelOf(input) {
      const label = this.root.querySelector(`label[for="${input.id}"]`);
      if (!label) return input.value || "";
      const v = label.querySelector(".option_value");
      return (v ? v.textContent : label.getAttribute("aria-label") || label.textContent).trim();
    }
    checkedLabel(pos) {
      const fs = this.fieldset(pos);
      const input = fs && fs.querySelector('input[type="radio"]:checked');
      return input ? this.labelOf(input) : "";
    }
    /* Coche la valeur ; renvoie l'input coché s'il a changé, true s'il l'était déjà, false sinon. */
    check(pos, label) {
      const fs = this.fieldset(pos);
      if (!fs) return false;
      for (const input of fs.querySelectorAll('input[type="radio"]')) {
        if (this.labelOf(input) === label) {
          if (input.checked) return true;
          input.checked = true;
          return input;
        }
      }
      return false;
    }
    /* Le sélecteur du thème (<variant-input>) est chargé à la demande, comme le fait kt-measure. */
    picker() {
      if (!customElements.get("variant-input")) {
        Promise.all([import("@NextSkyTheme/add-to-cart"), import("@NextSkyTheme/variant-swatch")]).catch(() => {});
      }
      return customElements.whenDefined("variant-input");
    }
    select(pos, label) {
      const r = this.check(pos, label);
      if (r instanceof HTMLElement) {
        this.picker().then(() => r.dispatchEvent(new Event("change", { bubbles: true })));
      }
      return r !== false;
    }
    currentVariant() {
      const el = this.root.querySelector(".productVariantSelected");
      try {
        return el ? JSON.parse(el.textContent) : null;
      } catch (e) {
        return null;
      }
    }
    observe() {
      const block = this.root.querySelector(".block-product__variant-picker");
      if (!block) return;
      new MutationObserver(() => this.refresh()).observe(block, { childList: true });
    }

    /* ---------- « Faites votre kit » ---------- */
    bindKit() {
      if (!this.kit) return;
      this.kit.querySelectorAll(".is-carousel").forEach((c) => this.dragScroll(c));
      this.kit.addEventListener("click", (e) => {
        const b = e.target.closest("[data-pos][data-value]");
        if (!b || !this.kit.contains(b)) return;
        const pos = Number(b.dataset.pos);
        this.note = "";
        this.mark(pos, b.dataset.value);
        this.select(pos, b.dataset.value);
        this.updateRecap();
      });
    }
    rows() {
      return this.kit ? Array.from(this.kit.querySelectorAll("[data-kt-row]")) : [];
    }
    /* Montre la valeur choisie dans sa ligne : contour noir, pastille entourée, bouton gris foncé. */
    mark(pos, value) {
      const row = this.kit && this.kit.querySelector(`[data-kt-row="${pos}"]`);
      if (!row || !value) return;
      row.querySelectorAll("[data-pos][data-value]").forEach((b) => {
        const on = b.dataset.value === value;
        b.setAttribute("aria-checked", String(on));
        b.classList.toggle("selected", on);
        const li = b.closest(".cs-sibling-item");
        if (li) li.classList.toggle("active-swatch", on);
      });
      const shown = row.querySelector("[data-kt-value]");
      if (shown) shown.textContent = value;
      const picked = row.querySelector('.is-carousel [aria-checked="true"]');
      if (picked) this.reveal(picked);
    }
    /* Carrousel : la carte choisie entre dans le cadre, avec la suivante à moitié visible. */
    reveal(tile) {
      const c = tile.parentElement;
      const left = tile.offsetLeft - c.offsetLeft;
      const right = left + tile.offsetWidth;
      if (left < c.scrollLeft) c.scrollLeft = Math.max(0, left - tile.offsetWidth / 2);
      else if (right > c.scrollLeft + c.clientWidth) c.scrollLeft = right - c.clientWidth + tile.offsetWidth / 2;
    }
    /* À la souris, le carrousel se fait glisser ; au doigt et au pavé tactile, il défile tout seul. */
    dragScroll(c) {
      let x0 = 0, s0 = 0, moved = false, down = false;
      c.addEventListener("pointerdown", (e) => {
        if (e.pointerType !== "mouse" || e.button !== 0) return;
        down = true;
        moved = false;
        x0 = e.clientX;
        s0 = c.scrollLeft;
      });
      c.addEventListener("pointermove", (e) => {
        if (!down) return;
        const dx = e.clientX - x0;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          c.classList.add("is-dragging");
        }
        if (moved) c.scrollLeft = s0 - dx;
      });
      const end = () => {
        down = false;
        if (moved) setTimeout(() => c.classList.remove("is-dragging"), 0);
      };
      c.addEventListener("pointerup", end);
      c.addEventListener("pointerleave", end);
      c.addEventListener("click", (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      }, true);
    }
    syncKit() {
      this.rows().forEach((row) => this.mark(Number(row.dataset.ktRow), this.checkedLabel(Number(row.dataset.ktRow))));
    }

    /* ---------- longueur ---------- */
    setupLength() {
      const opt = (this.data.options || []).find((o) => o.position === this.lenPos);
      this.size = this.querySelector("[data-kt-acc-size]");
      if (!opt || !this.size) return;
      this.paliers = opt.values
        .map((label) => ({ label, cm: parseInt(label, 10) }))
        .filter((p) => p.cm > 0)
        .sort((a, b) => a.cm - b.cm);
      if (!this.paliers.length) return;
      const q = (s) => this.size.querySelector(s);
      this.cards = { std: q('[data-kt-acc-card="std"]'), custom: q('[data-kt-acc-card="custom"]') };
      this.stdButtons = Array.from(this.size.querySelectorAll(".std-size-btn[data-cm]"));
      this.input = q(".kt-acc__input");
      this.unitSel = q(".kt-acc__unit");
      this.unitLabel = q(".kt-acc__unit-label");
      this.msg = q(".kt-acc__msg");
      this.prop = q(".kt-acc__prop");
      if (this.unitSel) this.unitSel.value = this.unit;
      this.stdButtons.forEach((b) => b.addEventListener("click", () => this.chooseStandard(Number(b.dataset.cm))));
      this.size.querySelectorAll("[data-kt-acc-head]").forEach((head) => {
        const go = () => this.chooseCard(head.dataset.ktAccHead);
        head.addEventListener("click", go);
        head.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            go();
          }
        });
      });
      let timer;
      this.input.addEventListener("input", () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.applyCote(), 350);
      });
      this.input.addEventListener("change", () => this.applyCote());
      if (this.unitSel) {
        this.unitSel.addEventListener("change", () => {
          this.unit = this.unitSel.value === "in" ? "in" : "cm";
          if (this.mode === "mesure" && this.cote) this.input.value = this.unit === "in" ? +(this.cote / 2.54).toFixed(1) : this.cote;
          this.paint();
        });
      }
    }
    show(cm, court) {
      if (this.unit !== "in") return `${cm} cm`;
      const inches = +(cm / 2.54).toFixed(1);
      return court ? `${inches} in` : `${inches} in (${cm} cm)`;
    }
    palierFor(cm) {
      return this.paliers.find((p) => p.cm >= cm) || null;
    }
    chooseCard(which) {
      if (which === "custom") {
        this.mode = "mesure";
        this.paint();
        this.input.focus();
        if (this.input.value) this.applyCote();
        return;
      }
      const std = this.stdButtons.map((b) => Number(b.dataset.cm));
      const current = parseInt(this.checkedLabel(this.lenPos), 10);
      this.chooseStandard(std.includes(current) ? current : std[0] || current);
    }
    chooseStandard(cm) {
      if (!cm) return;
      this.mode = "standard";
      this.cote = cm;
      const p = this.palierFor(cm);
      if (p) this.select(this.lenPos, p.label);
      this.paint();
      this.updateRecap();
    }
    readInput() {
      const raw = parseFloat(String(this.input.value).replace(",", "."));
      if (!(raw > 0)) return null;
      return Math.round(this.unit === "in" ? raw * 2.54 : raw);
    }
    applyCote() {
      this.mode = "mesure";
      const cm = this.readInput();
      this.cote = cm;
      const p = cm ? this.palierFor(cm) : null;
      if (p && cm >= MIN_CM) this.select(this.lenPos, p.label);
      this.paint();
      this.updateRecap();
    }
    lengthState() {
      if (!this.lenPos || !this.paliers) return { ok: true };
      const max = this.paliers[this.paliers.length - 1].cm;
      if (this.mode === "standard") return { ok: true, palier: this.palierFor(this.cote || 0) };
      if (!this.cote) return { ok: false, msg: this.t.manque };
      if (this.cote < MIN_CM) return { ok: false, msg: fmt(this.t.trop_court, { min: this.show(MIN_CM) }) };
      if (this.cote > max) return { ok: false, msg: fmt(this.t.trop_long, { max: this.show(max) }) };
      return { ok: true, palier: this.palierFor(this.cote) };
    }
    longueur() {
      const st = this.lengthState();
      if (!st.ok) return null;
      return this.mode === "mesure" ? this.cote : st.palier ? st.palier.cm : parseInt(this.checkedLabel(this.lenPos), 10) || null;
    }
    paint() {
      if (!this.lenPos || !this.cards) return;
      const st = this.lengthState();
      const mesure = this.mode === "mesure";
      Object.entries(this.cards).forEach(([k, card]) => {
        const on = (k === "custom") === mesure;
        card.dataset.on = String(on);
        const head = card.querySelector("[data-kt-acc-head]");
        head.setAttribute("aria-checked", String(on));
        head.tabIndex = on ? 0 : -1;
        card.querySelector(".kt-size-card__body").hidden = !on;
      });
      this.stdButtons.forEach((b) => {
        const cm = Number(b.dataset.cm);
        b.classList.toggle("selected", !mesure && cm === this.cote);
        b.querySelector(".std-size-value").textContent = this.show(cm, true);
      });
      if (this.unitLabel) this.unitLabel.textContent = this.unit;
      const palier = st.palier ? st.palier.cm : parseInt(this.checkedLabel(this.lenPos), 10);
      let msg = "";
      if (!st.ok) msg = st.msg;
      else if (mesure) msg = fmt(this.t.palier, { cote: this.show(this.cote), palier: this.show(palier) });
      else if (palier) msg = fmt(this.t.standard_msg, { palier: this.show(palier) });
      const longueur = mesure ? this.cote : palier;
      if (this.note) msg = this.note + " " + msg;
      this.msg.textContent = msg.trim();
      this.msg.classList.toggle("is-error", !st.ok);
      const send = mesure && st.ok;
      this.prop.disabled = !send;
      this.prop.value = send ? `${this.cote} cm` : "";
    }

    /* ---------- récapitulatif ---------- */
    setupRecap() {
      const recap = this.root.querySelector(".kt-recap");
      const list = recap && recap.querySelector(".kt-recap__list");
      const tpl = list && list.querySelector('[data-kt-recap-row="fixation"]');
      if (!tpl) return;
      const before = list.querySelector('[data-kt-recap-row="quantity"]');
      this.recapRows = [];
      const make = (key, label, target) => {
        const row = tpl.cloneNode(true);
        row.dataset.ktRecapRow = "acc-" + key;
        row.hidden = true;
        const dt = row.querySelector("dt");
        (dt.querySelector(".kt-recap__font") || dt).textContent = label;
        const edit = row.querySelector(".kt-recap__edit");
        if (edit) {
          if (target) edit.dataset.ktRecapTarget = target;
          else edit.closest("dd").remove();
        }
        list.insertBefore(row, before);
        return row;
      };
      this.rows().forEach((r) => {
        if (r.dataset.ktKind === "color") return;
        this.recapRows.push({ pos: Number(r.dataset.ktRow), row: make(r.dataset.ktRow, r.dataset.ktLabel, "kit") });
      });
      if (this.lenPos) this.recapLength = make("length", this.t.length, "length");
      if (this.data.composition && this.data.composition.v) this.recapColis = make("colis", this.t.colis, null);
      this.recapColor = list.querySelector('[data-kt-recap-row="color"]');
      // La quantité se compte en pièces, pas en pans de rideau.
      const unit = list.querySelector('[data-kt-recap-row="quantity"] .kt-recap__unit');
      if (unit) unit.remove();
    }
    setRow(row, text) {
      if (!row) return;
      row.hidden = !text;
      const el = row.querySelector(".kt-recap__text");
      if (el && text) el.textContent = text;
    }
    updateRecap() {
      if (!this.recapRows) return;
      this.recapRows.forEach(({ pos, row }) => this.setRow(row, this.checkedLabel(pos)));
      if (this.recapLength) {
        const l = this.longueur();
        this.setRow(this.recapLength, l ? this.show(l, true) : "");
      }
      // Couleur portée par une option (embrasses, pièces) : la ligne Couleur suit le choix.
      const colorRow = this.kit && this.kit.querySelector('[data-kt-kind="color"]');
      if (colorRow && this.recapColor) {
        const v = this.checkedLabel(Number(colorRow.dataset.ktRow));
        if (v) this.recapColor.querySelector(".kt-recap__text").textContent = v;
        const dot = this.recapColor.querySelector(".kt-recap__dot");
        const src = colorRow.querySelector(".active-swatch .cs-sibling-color-dot");
        if (dot && src) dot.setAttribute("style", src.getAttribute("style") || "");
        // Couleurs en photo (embrasses) : pas de pastille juste, on n'en montre pas.
        else if (dot) dot.style.display = "none";
      }
      this.renderComposition();
    }
    renderComposition() {
      const row = this.recapColis;
      const comp = this.data.composition;
      if (!row || !comp || !comp.v) return;
      const v = this.currentVariant();
      const lines = v && comp.v[String(v.id)];
      row.hidden = !lines;
      if (!lines) return;
      const text = row.querySelector(".kt-recap__text");
      const ul = document.createElement("ul");
      ul.className = "kt-recap__colis";
      lines.forEach((i) => {
        const li = document.createElement("li");
        li.textContent = comp.l[i];
        ul.appendChild(li);
      });
      if (this.t.colis_note) {
        const li = document.createElement("li");
        li.className = "kt-recap__colis-note";
        li.textContent = this.t.colis_note;
        ul.appendChild(li);
      }
      text.replaceChildren(ul);
    }

    /* ---------- après chaque rechargement du sélecteur ---------- */
    refresh() {
      this.syncKit();
      if (this.lenPos && this.cards && this.cote == null) {
        const current = parseInt(this.checkedLabel(this.lenPos), 10);
        if (current) this.cote = current;
      }
      this.updateButton();
      this.paint();
      this.updateRecap();
    }
    /* Le bouton d'achat affiche « Ajouter au panier — prix » ; le thème ne le change qu'avec la quantité. */
    updateButton() {
      const v = this.currentVariant();
      const el = this.root.querySelector(".product-detail__buy-buttons .total-price__detail") || this.root.querySelector(".total-price__detail");
      if (!v || !el || typeof v.price !== "number") return;
      const form = el.closest("form");
      const qty = Number(((form && form.querySelector('input[name="quantity"]')) || {}).value || 1);
      el.setAttribute("data-total-price", v.price);
      el.textContent = money(v.price * qty);
      // Le total du récapitulatif suit le bouton (son script ne relit le bouton qu'à une interaction).
      const total = this.root.querySelector(".kt-recap__total");
      if (total) total.textContent = money(v.price * qty);
    }

    /* ---------- d'une couleur à l'autre ---------- */
    bindSiblings() {
      this.root.addEventListener(
        "click",
        (e) => {
          const a = e.target.closest('[class*="sibling"] a[href]');
          if (!a || !this.root.contains(a)) return;
          const url = new URL(a.getAttribute("href"), location.origin);
          if (url.pathname === location.pathname) return;
          (this.data.options || []).forEach((o) => {
            if (o.position === this.lenPos) return;
            const label = this.checkedLabel(o.position);
            if (label) url.searchParams.set("kt_o" + o.position, label);
          });
          if (this.lenPos && this.cote) url.searchParams.set(this.mode === "mesure" ? "kt_c" : "kt_l", this.cote);
          a.setAttribute("href", url.pathname + url.search);
        },
        true
      );
    }
    applyFromUrl() {
      const params = new URLSearchParams(location.search);
      if (!PARAMS.some((k) => params.has(k))) return false;
      let last = null;
      const manquants = [];
      (this.data.options || []).forEach((o) => {
        const want = params.get("kt_o" + o.position);
        if (!want || o.position === this.lenPos) return;
        const r = this.check(o.position, want);
        if (r === false) manquants.push(want);
        else if (r instanceof HTMLElement) last = r;
      });
      if (this.lenPos && this.paliers) {
        const c = parseInt(params.get("kt_c"), 10);
        const l = parseInt(params.get("kt_l"), 10);
        if (c > 0) {
          this.mode = "mesure";
          this.cote = c;
          this.input.value = this.unit === "in" ? +(c / 2.54).toFixed(1) : c;
        } else if (l > 0) {
          this.mode = "standard";
          this.cote = l;
        }
        const p = this.cote ? this.palierFor(this.cote) : null;
        if (p) {
          const r = this.check(this.lenPos, p.label);
          if (r instanceof HTMLElement) last = r;
        }
      }
      if (manquants.length) this.note = fmt(this.t.remplace, { choix: manquants.join(", ") });
      if (last) this.picker().then(() => last.dispatchEvent(new Event("change", { bubbles: true })));
      const clean = new URL(location.href);
      PARAMS.forEach((k) => clean.searchParams.delete(k));
      history.replaceState(history.state, "", clean.pathname + clean.search + clean.hash);
      this.syncKit();
      this.paint();
      this.updateRecap();
      return true;
    }

    /* ---------- envoi au panier ---------- */
    guardSubmit() {
      const form = document.getElementById(this.dataset.form) || (this.root.querySelector(".total-price__detail") || this).closest("form");
      if (!form || !this.lenPos) return;
      form.addEventListener(
        "submit",
        (e) => {
          const st = this.lengthState();
          if (st.ok) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          this.paint();
          (this.size || this).scrollIntoView({ block: "center", behavior: "smooth" });
        },
        true
      );
    }
  }

  customElements.define("kt-accessoire", KtAccessoire);
})();
